// AGC: Blockchain Tour Slides - Marketing-focused explanation of on-chain features (v6.10.1)
// AGC: Clean, minimalist slide-based presentation with next/previous navigation

import React, { useState } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'
import { ContractLink } from '../common/ContractLink'

interface BlockchainTourSlidesProps {
  isOpen: boolean
  onClose: () => void
}

// AGC: Slide data - marketing-focused, non-technical explanations
const slides = [
  {
    id: 1,
    title: 'Built on Base',
    subtitle: 'Coinbase\'s Ethereum Layer 2',
    icon: '🔗',
    content: (
      <div className="space-y-4">
        <p className="text-lg text-slate-200 leading-relaxed">
          Origami Battles runs on <span className="text-cyan-400 font-semibold">Base</span>, Coinbase's secure and fast blockchain network. This Layer 2 solution provides the perfect balance of security, speed, and affordability for blockchain gaming.
        </p>
        <div className="space-y-0">
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">⚡</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Lightning Fast</p>
              <p className="text-sm text-slate-400 leading-relaxed">Transactions confirm in ~2 seconds, making battles feel instant. No more waiting minutes for Ethereum confirmations!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">💰</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Ultra Low Fees</p>
              <p className="text-sm text-slate-400 leading-relaxed">100x cheaper than Ethereum - perfect for micro-transactions. Battle fees are just pennies instead of dollars!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10 last:border-0">
            <span className="text-2xl">🛡️</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Secure & Reliable</p>
              <p className="text-sm text-slate-400 leading-relaxed">Backed by Coinbase's infrastructure and secured by Ethereum's mainnet. Your assets are as safe as they can be!</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 2,
    title: '100% On-Chain Battles',
    subtitle: 'Every Battle is Verifiable',
    icon: '⚔️',
    content: (
      <div className="space-y-4">
        <p className="text-lg text-slate-200 leading-relaxed">
          Every battle outcome, every win, every loss - <span className="text-purple-400 font-semibold">permanently recorded</span> on the blockchain. This means complete transparency and verifiability for all players.
        </p>
        <div className="space-y-0">
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">✅</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Fully Transparent</p>
              <p className="text-sm text-slate-400 leading-relaxed">View any battle on BaseScan via <ContractLink name="P2P Manager V2" className="text-cyan-400 hover:text-cyan-300" /> or <ContractLink name="PvE Manager V7" className="text-cyan-400 hover:text-cyan-300" /> - no hidden mechanics, no server-side manipulation. Every move is public and verifiable.</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🔒</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Immutable Records</p>
              <p className="text-sm text-slate-400 leading-relaxed">Your battle history can never be changed or deleted. Once recorded, it's permanent and tamper-proof.</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10 last:border-0">
            <span className="text-2xl">📊</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">True Ownership</p>
              <p className="text-sm text-slate-400 leading-relaxed">Your stats, XP (via <ContractLink name="Player XP" className="text-cyan-400 hover:text-cyan-300" />), and achievements are truly yours. They exist on-chain, independent of any server or company.</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 3,
    title: 'Provably Fair Randomness',
    subtitle: 'Chainlink VRF Technology',
    icon: '🎲',
    content: (
      <div className="space-y-4">
        <p className="text-lg text-slate-200 leading-relaxed">
          We use <ContractLink name="RNG Beacon" className="text-amber-400 font-semibold" /> (Chainlink VRF), the industry standard for fair randomness. This ensures that all game mechanics involving chance are provably fair and cannot be manipulated.
        </p>
        <div className="space-y-0">
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🎯</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Tamper-Proof</p>
              <p className="text-sm text-slate-400 leading-relaxed">No one can manipulate rarity rolls or buff activations. Not even the developers can influence the outcomes!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🔍</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Verifiable</p>
              <p className="text-sm text-slate-400 leading-relaxed">Every random event can be verified on-chain. Check any battle's randomness source on <ContractLink name="RNG Beacon" className="text-cyan-400 hover:text-cyan-300" />!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10 last:border-0">
            <span className="text-2xl">⚖️</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Fair for Everyone</p>
              <p className="text-sm text-slate-400 leading-relaxed">Same odds for all players - no advantages, no pay-to-win manipulation. True fairness guaranteed by cryptography.</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 4,
    title: 'Deflationary Token Economy',
    subtitle: 'Tokens Get Burned Forever',
    icon: '🔥',
    content: (
      <div className="space-y-4">
        <p className="text-lg text-slate-200 leading-relaxed">
          Every battle <span className="text-red-400 font-semibold">burns tokens permanently</span>, creating scarcity and value. This deflationary mechanism ensures that as the game grows, tokens become more valuable.
        </p>
        <div className="space-y-0">
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">💸</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">15% Burn Rate</p>
              <p className="text-sm text-slate-400 leading-relaxed">15 tokens burned per battle - gone forever. This creates permanent scarcity as more battles are played.</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🎨</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">NFT Consumption</p>
              <p className="text-sm text-slate-400 leading-relaxed"><ContractLink name="Buff1155" className="text-cyan-400 hover:text-cyan-300" /> and <ContractLink name="Potion1155" className="text-cyan-400 hover:text-cyan-300" /> are consumed in battles, reducing supply and increasing value of remaining NFTs.</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10 last:border-0">
            <span className="text-2xl">📈</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Increasing Scarcity</p>
              <p className="text-sm text-slate-400 leading-relaxed">More players = more burns = higher value. The tokenomics are designed to reward early adopters!</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 5,
    title: 'Earn While You Play',
    subtitle: 'Staking & Revenue Sharing',
    icon: '💎',
    content: (
      <div className="space-y-4">
        <p className="text-lg text-slate-200 leading-relaxed">
          Stake your tokens via <ContractLink name="StakingManager V4" className="text-emerald-400 font-semibold" /> and earn <span className="text-emerald-400 font-semibold">passive income</span> from marketplace revenue. The more you stake, the more you earn - it's that simple!
        </p>
        <div className="space-y-0">
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">💵</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Weekly Rewards</p>
              <p className="text-sm text-slate-400 leading-relaxed">Earn ETH, USDC, and tokens from marketplace sales via <ContractLink name="StakingRevenueShare V4" className="text-cyan-400 hover:text-cyan-300" />. Rewards are distributed automatically every week based on your stake percentage.</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🎁</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">50% Marketplace Discount</p>
              <p className="text-sm text-slate-400 leading-relaxed">Stake 500+ tokens and get half off all NFTs. This makes building your collection much more affordable!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10 last:border-0">
            <span className="text-2xl">📊</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Proportional Share</p>
              <p className="text-sm text-slate-400 leading-relaxed">The more you stake, the more you earn. Your rewards are calculated based on your percentage of total staked tokens.</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 6,
    title: 'True NFT Ownership',
    subtitle: 'Your Assets, Your Control',
    icon: '🎨',
    content: (
      <div className="space-y-4">
        <p className="text-lg text-slate-200 leading-relaxed">
          All buffs and potions are <span className="text-pink-400 font-semibold">real NFTs</span> stored in your wallet. You truly own them - they're not just database entries on our servers!
        </p>
        <div className="space-y-0">
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">👛</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Wallet Storage</p>
              <p className="text-sm text-slate-400 leading-relaxed">Your NFTs live in your wallet, not our servers. Even if we shut down, your NFTs remain yours forever.</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🔄</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Tradeable</p>
              <p className="text-sm text-slate-400 leading-relaxed">Buy, sell, or trade on any NFT marketplace. Your NFTs are standard ERC1155 tokens (<ContractLink name="Buff1155" className="text-cyan-400 hover:text-cyan-300" />, <ContractLink name="Potion1155" className="text-cyan-400 hover:text-cyan-300" />) compatible with all major marketplaces!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10 last:border-0">
            <span className="text-2xl">♾️</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Permanent</p>
              <p className="text-sm text-slate-400 leading-relaxed">Your NFTs exist as long as the blockchain exists. They're permanent, immutable, and truly yours - no expiration dates!</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 7,
    title: 'Quest & Rewards Flywheel',
    subtitle: 'Play, Earn, Repeat',
    icon: '🎯',
    content: (
      <div className="space-y-4">
        <p className="text-lg text-slate-200 leading-relaxed">
          A <span className="text-cyan-400 font-semibold">self-sustaining economy</span> where playing battles earns you rewards to play more. This creates a positive feedback loop that benefits active players!
        </p>
        <div className="space-y-0">
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🎮</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Play Battles</p>
              <p className="text-sm text-slate-400 leading-relaxed">Win battles to earn tokens and XP. Every victory brings you closer to leveling up and earning more rewards!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">✅</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Complete Quests</p>
              <p className="text-sm text-slate-400 leading-relaxed">Daily, weekly, and monthly quests reward 10-500 tokens. Regular play is rewarded with consistent income!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10 last:border-0">
            <span className="text-2xl">🔄</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Reinvest & Grow</p>
              <p className="text-sm text-slate-400 leading-relaxed">Buy NFTs, stake tokens, and keep playing. The cycle continues - earn, invest, earn more, repeat!</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 8,
    title: '50/50 Revenue Share',
    subtitle: 'Automated On-Chain Distribution',
    icon: '⚖️',
    content: (
      <div className="space-y-4">
        <p className="text-lg text-slate-200 leading-relaxed">
          Marketplace revenue is <span className="text-emerald-400 font-semibold">automatically split 50/50</span> between stakers and the game - all on-chain and immutable. This creates a fair, transparent revenue sharing model!
        </p>
        <div className="space-y-0">
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">👥</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">50% to Stakers</p>
              <p className="text-sm text-slate-400 leading-relaxed">Players who stake tokens earn proportional share of marketplace revenue. The more you stake, the bigger your slice of the pie!</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🎮</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">50% to Game</p>
              <p className="text-sm text-slate-400 leading-relaxed"><ContractLink name="StakingMirror" className="text-cyan-400 hover:text-cyan-300" /> automatically maintains game's 50% stake balance, ensuring the <ContractLink name="Game Treasury" className="text-cyan-400 hover:text-cyan-300" /> grows alongside player rewards.</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10">
            <span className="text-2xl">🤖</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Fully Automated</p>
              <p className="text-sm text-slate-400 leading-relaxed"><ContractLink name="StakingManager V4" className="text-cyan-400 hover:text-cyan-300" /> and <ContractLink name="StakingRevenueShare V4" className="text-cyan-400 hover:text-cyan-300" /> handle everything - no manual intervention needed. The system runs itself, ensuring fairness and transparency.</p>
            </div>
          </div>
          <div className="flex items-start gap-1.5 py-2 border-b border-white/10 last:border-0">
            <span className="text-2xl">🔒</span>
            <div className="flex-1">
              <p className="text-slate-300 font-semibold text-base mb-0.5">Immutable & Transparent</p>
              <p className="text-sm text-slate-400 leading-relaxed">All revenue distribution is on-chain and verifiable on BaseScan. You can audit every transaction and verify the 50/50 split yourself!</p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
]

// AGC: Click sound effect
const playClickSound = () => {
  try {
    const audio = new Audio('/sounds/click.mp3')
    audio.volume = 0.5
    audio.play().catch(() => {
      // AGC: Silently fail if audio can't play
    })
  } catch (error) {
    // AGC: Silently fail if audio creation fails
  }
}

export const BlockchainTourSlides: React.FC<BlockchainTourSlidesProps> = ({ isOpen, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0)

  // AGC: Reset to first slide when modal opens
  React.useEffect(() => {
    if (isOpen) {
      setCurrentSlide(0)
    }
  }, [isOpen])

  // AGC: Handle keyboard navigation
  React.useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        handlePrevious()
      } else if (e.key === 'ArrowRight') {
        handleNext()
      } else if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, currentSlide])

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      playClickSound()
      setCurrentSlide(currentSlide + 1)
    }
  }

  const handlePrevious = () => {
    if (currentSlide > 0) {
      playClickSound()
      setCurrentSlide(currentSlide - 1)
    }
  }

  const handleClose = () => {
    playClickSound()
    onClose()
  }

  if (!isOpen) {
    return null
  }

  const slide = slides[currentSlide]
  const isFirstSlide = currentSlide === 0
  const isLastSlide = currentSlide === slides.length - 1

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      {/* AGC: Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={handleClose}
      />
      
      {/* AGC: Modal content - Fixed size: full width (with padding), 90% height */}
      <div className="relative w-full max-w-2xl h-[90vh] bg-gradient-to-b from-slate-900 to-slate-950 border border-cyan-500/30 rounded-3xl shadow-2xl shadow-cyan-500/10 overflow-hidden animate-in fade-in zoom-in duration-300 flex flex-col">
        {/* AGC: Decorative gradient top */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-cyan-500/10 via-purple-500/10 to-transparent pointer-events-none" />
        
        {/* AGC: Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* AGC: Slide content - Scrollable area */}
        <div className="relative flex-1 overflow-y-auto pt-8 px-8 pb-4">
          {/* AGC: Slide header */}
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">{slide.icon}</div>
            <h2 className="text-2xl font-bold text-white mb-1">
              {slide.title}
            </h2>
            <p className="text-slate-400 text-sm">
              {slide.subtitle}
            </p>
          </div>

          {/* AGC: Slide content - Uses full available space */}
          <div className="flex items-start justify-center py-4">
            <div className="w-full max-w-3xl">
              {slide.content}
            </div>
          </div>
        </div>

        {/* AGC: Navigation footer - Fixed at bottom */}
        <div className="bg-slate-900/95 border-t border-slate-800 px-8 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            {/* AGC: Previous button */}
            <button
              onClick={handlePrevious}
              disabled={isFirstSlide}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                isFirstSlide
                  ? 'opacity-50 cursor-not-allowed text-slate-500'
                  : 'bg-slate-800 hover:bg-slate-700 text-white'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Previous</span>
            </button>

            {/* AGC: Slide indicators */}
            <div className="flex items-center gap-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    playClickSound()
                    setCurrentSlide(index)
                  }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide
                      ? 'bg-cyan-400 w-8'
                      : 'bg-slate-600 hover:bg-slate-500'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* AGC: Next button */}
            <button
              onClick={isLastSlide ? handleClose : handleNext}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-white transition-all shadow-lg shadow-purple-500/20"
            >
              <span className="hidden sm:inline">{isLastSlide ? 'Finish' : 'Next'}</span>
              {!isLastSlide && <ChevronRight className="w-5 h-5" />}
            </button>
          </div>

          {/* AGC: Slide counter */}
          <div className="text-center mt-2">
            <span className="text-xs text-slate-500">
              {currentSlide + 1} of {slides.length}
            </span>
          </div>
        </div>

        {/* AGC: Decorative corner accents */}
        <div className="absolute top-0 left-0 w-16 h-16 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-3xl pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-16 h-16 border-r-2 border-b-2 border-purple-500/30 rounded-br-3xl pointer-events-none" />
      </div>
    </div>
  )
}

export default BlockchainTourSlides

